/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import '../../styles/components/AsperaTransferActivity.scss';
import { language } from '../../services/LanguageService';
import { SubManager } from '../../classes/SubManager';
import { monitorTransfers, getAllCurrentTransfers, getTransferSystemStatus, openConnectPreferences, clearInactiveTransfers, showConnectInstallFlow, getTransferSystemData, runningWithAsperaMobile, getInactiveTransfers } from '../../helpers/transfer';
import { supportMobileAppAdvertising, openInstallMobileApp } from '../../helpers/starterHelpers';
import { getEnvironmentVar, isMobile } from '../../helpers/helpers';
import { AsperaError } from '../../classes/AsperaError';
import { Dropdown, Loading, Link, Button, InlineNotification, getIconComponent, getIcon, Theme } from '../Carbon';
import { AsperaTransfer } from '../../classes/AsperaTransfer';
import { AsperaTransferActivityItem } from '../AsperaTransferActivityItem';
import { AsperaEmptyState } from '../AsperaEmptyState';
import { AsperaSlideOverPanel } from '../AsperaSlideOverPanel';
import { tracking } from '../../services/TrackingService';
import { AsperaForm } from '../AsperaForm';
import { ModalTransferDetails } from '../ModalTransferDetails';
import { modal } from '../../services/ModalService';
import { Subject } from 'rxjs';
import { changeTransferClient } from '../../helpers/transfer';
/**
 * React Component
 *
 * The AsperaTransferActivity component renders the transfer monitor on the right side of the page
 *
 * Issues: onClose is required for SidePanel even though we don't use their close
 * Issues: Dropdown items type enforces only string[] but Carbon docs accept anything.
 * Issues: Dropdown does not support className so need to target generics in styling
 * Issues: EmptyState does not pass through className
 *
 * @example
 * ```javascript
 * <AsperaTransferActivity />
 * ```
 */
export class AsperaTransferActivity extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            loading: true,
        };
        this.subManager = new SubManager();
        this.transfers = [];
        this.transferUpdate = new Subject();
        this.settings = {
            disableConnect: false,
        };
        this.filterOptions = {
            activity: '',
            status: '',
        };
        this.activityOptions = [
            { text: language.get('transferActivity.allActivity'), id: '' },
            { text: language.get('transferActivity.upload'), id: 'send' },
            { text: language.get('transferActivity.download'), id: 'receive' },
        ];
        this.statusOptions = [
            { text: language.get('transferActivity.allStatus'), id: '' },
            { text: language.get('transferActivity.failed'), id: 'failed' },
            { text: language.get('transferActivity.stopped'), id: 'cancelled' },
            { text: language.get('transferActivity.queued'), id: 'queued' },
        ];
        this.clearInactive = () => {
            const { onClearInactive } = this.props;
            tracking.track('Started Process', { successFlag: true, process: 'Clicked Clear Inactive Transfers', processType: 'transfer' });
            clearInactiveTransfers(onClearInactive);
        };
        this.changeDownloadLocation = () => {
            tracking.track('Started Process', { successFlag: true, process: 'Clicked Change Download Location', processType: 'transfer' });
            openConnectPreferences();
        };
        this.downloadMobileApp = () => {
            tracking.track('Started Process', { successFlag: true, process: 'Clicked Install Mobile App', processType: 'mobile' });
            openInstallMobileApp(false);
        };
        this.openMobileApp = () => {
            tracking.track('Started Process', { successFlag: true, process: 'Clicked Open Mobile App', processType: 'mobile' });
            openInstallMobileApp(true);
        };
        this.openTransferInfo = (transfer) => {
            const modalInfo = modal.prepare();
            const modalProps = {
                modalHeading: transfer.transferTitle || language.get('transferActivity.details'),
                passiveModal: true,
            };
            this.subManager.setSubscription(modal.create(modalInfo.id, modalProps, React.createElement(ModalTransferDetails, { transfer: transfer, initialOpen: !transfer.isActive ? 'INFO' : undefined, modalClose: modalInfo.callback, transferUpdateObservable: this.transferUpdate.asObservable() })).subscribe({
                next: () => { },
                error: () => { },
            }));
        };
        this.changeActivity = (data) => {
            this.filterOptions.activity = data.selectedItem.id;
            this.setState({});
        };
        this.changeStatus = (data) => {
            this.filterOptions.status = data.selectedItem.id;
            this.setState({});
        };
        this.installConnect = () => {
            tracking.track('Started Process', {
                process: 'Connect Install via Transfer Activity',
                successFlag: true,
                processType: 'connect',
            });
            showConnectInstallFlow();
        };
    }
    get panelOptions() {
        const { extraOptions } = this.props;
        return [
            { itemText: language.get('transferActivity.clearInactiveTransfers'), onClick: this.clearInactive, hidden: !this.transfers.length },
            { itemText: language.get('transferActivity.reloadConnect'), onClick: this.installConnect, hidden: (getTransferSystemStatus().connectAvailable || !!tracking.getUserPreference('disableConnect') || isMobile()) },
            { itemText: language.get('transferActivity.installMobileApp'), onClick: this.downloadMobileApp, hidden: !supportMobileAppAdvertising() },
            { itemText: language.get('transferActivity.openMobileApp'), onClick: this.openMobileApp, hidden: !supportMobileAppAdvertising() },
            { itemText: language.get('transferActivity.changeDownloadLocation'), onClick: this.changeDownloadLocation, disabled: !getTransferSystemStatus().connectAvailable },
            { itemText: language.get('transferActivity.changeTransferClient'), onClick: changeTransferClient, disabled: !getEnvironmentVar('support_desktop_sdk') },
            ...(extraOptions || []),
        ];
    }
    get advertisingItems() {
        const advertisements = [];
        // Desktop mode should not show advertisements
        if (getEnvironmentVar('desktop_mode')) {
            return [];
        }
        // Recent change in Carbon type made this freak out; spread had valid props but the change to the lib won't take onClick
        const shareButtonProps = {
            hasIconOnly: true,
            iconDescription: language.get('transferActivity.dismiss'),
            kind: 'ghost',
            renderIcon: getIconComponent('Close'),
            tooltipAlignment: 'end',
            tooltipPosition: 'bottom',
            size: 'sm',
        };
        const dismissItem = (key) => {
            tracking.track('Started Process', { successFlag: true, data: key, process: 'User Dismissed Transfer Activity Advertisement', processType: 'transfer' });
            tracking.setUserPreferences({ [key]: true }, true);
            this.setState({});
        };
        if (!tracking.getUserPreference('hideChangeDownloadLocationAd') && getTransferSystemStatus().connectAvailable) {
            advertisements.push(React.createElement("div", { className: "advertisement-item", key: "1" },
                React.createElement("div", { className: "cds--btn--field cds--btn--icon-only" }, getIcon('Download')),
                React.createElement(Link, { className: "clickable-part", onClick: this.changeDownloadLocation }, language.get('transferActivity.changeDownloadLocation')),
                React.createElement(Button, Object.assign({}, shareButtonProps, { onClick: () => {
                        dismissItem('hideChangeDownloadLocationAd');
                    } }))));
        }
        if (!tracking.getUserPreference('hideMobileDownloadAd') && supportMobileAppAdvertising()) {
            advertisements.push(React.createElement("div", { className: "advertisement-item", key: "2" },
                React.createElement("div", { className: "cds--btn--field cds--btn--icon-only" }, getIcon('ApplicationMobile')),
                React.createElement(Link, { className: "clickable-part", onClick: this.downloadMobileApp }, language.get('transferActivity.installMobileApp')),
                React.createElement(Button, Object.assign({}, shareButtonProps, { onClick: () => {
                        dismissItem('hideMobileDownloadAd');
                    } }))));
        }
        return advertisements;
    }
    get panelFooter() {
        if (getTransferSystemStatus().httpServerUrl) {
            this.settings.disableConnect = !!tracking.getUserPreference('disableConnect');
            const formProps = {
                light: true,
                formDetails: {
                    id: 'transfer-monitor-gateway-lock',
                    model: this.settings,
                    items: [
                        {
                            type: 'toggle',
                            pointer: 'disableConnect',
                            label: language.get('transferActivity.forceHttpGatewayUsage'),
                            toggleProps: {
                                labelA: language.get('transferActivity.forceHttpGatewayUsage'),
                                labelB: language.get('transferActivity.forceHttpGatewayUsage'),
                                hideLabel: true,
                            },
                            onChange: () => {
                                tracking.setUserPreferences({ disableConnect: this.settings.disableConnect }, true, true);
                                tracking.track('Updated Object', { object: 'User', process: 'Force to user Aspera HTTP gateway', custom: { connectDisabled: this.settings.disableConnect }, successFlag: true });
                            },
                        },
                    ],
                },
            };
            return (React.createElement("div", { className: "transfer-monitor--footer" },
                React.createElement(AsperaForm, Object.assign({}, formProps))));
        }
        return undefined;
    }
    getTransferList() {
        const { onCancel, onOpenLocalFolder, onRemove, onPause, onResume } = this.props;
        const filterFunction = (transfer) => {
            var _a;
            const statusTest = transfer.status === this.filterOptions.status;
            const activityTest = ((_a = (transfer.transfer_spec || transfer.start_spec)) === null || _a === void 0 ? void 0 : _a.direction) === this.filterOptions.activity;
            if (this.filterOptions.status && this.filterOptions.activity) {
                return (statusTest && activityTest);
            }
            else if (this.filterOptions.status) {
                return statusTest;
            }
            else if (this.filterOptions.activity) {
                return activityTest;
            }
            else {
                return true;
            }
        };
        const foundTransfers = this.transfers.filter(filterFunction);
        return (foundTransfers.length ? foundTransfers.map((transfer, index) => {
            return React.createElement(AsperaTransferActivityItem, { openTransferInfo: this.openTransferInfo, key: index, transfer: new AsperaTransfer(transfer), onCancel: onCancel, onOpenLocalFolder: onOpenLocalFolder, onRemove: onRemove, onPause: onPause, onResume: onResume });
        }) : this.getEmptyState(true));
    }
    getEmptyState(noResults) {
        const props = noResults ? {
            image: 'noSearchResults',
            title: language.get('transferActivity.noFilterResultsMessage'),
            format: 'sm',
        } : {
            image: 'default',
            title: language.get('transferActivity.noTransfersMessage'),
            format: 'sm',
        };
        return (React.createElement("div", { className: "no-transfers-state" },
            React.createElement(AsperaEmptyState, Object.assign({}, props, { "data-auto": "transfer-activity-empty-state" }))));
    }
    get transferError() {
        const connectDisabled = !!getTransferSystemData().disableConnect;
        if (supportMobileAppAdvertising()) {
            return React.createElement("div", { className: "error-area" },
                React.createElement(InlineNotification, { lowContrast: true, kind: "error", title: language.get('transferSystem.mobileAppRequired'), subtitle: (React.createElement(React.Fragment, null,
                        React.createElement("span", null, language.get('transferSystem.toTransferInstallMobileApp')),
                        !connectDisabled && React.createElement(Link, { className: "action-link", onClick: this.downloadMobileApp }, language.get('transferActivity.installMobileApp')))) }));
        }
        return React.createElement("div", { className: "error-area" },
            React.createElement(InlineNotification, { lowContrast: true, kind: "error", title: language.get(`transferSystem.${connectDisabled ? 'transferSystemUnavailable' : 'connectRequired'}`), subtitle: (React.createElement(React.Fragment, null,
                    React.createElement("span", null, language.get(`transferSystem.${connectDisabled ? 'noTransferSystemAvailable' : 'toTransferInstallConnect'}`)),
                    !connectDisabled && React.createElement(Link, { className: "action-link", onClick: this.installConnect }, language.get('transferSystem.installTransferClient')))) }));
    }
    get mobileWarning() {
        return React.createElement("div", { className: "error-area" },
            React.createElement(InlineNotification, { lowContrast: true, kind: "info", title: language.get('transferSystem.mobileTransfers'), subtitle: language.get('transferSystem.mobileTransfersHelper') }));
    }
    getBody() {
        const { loading } = this.state;
        const { fullPageMode } = this.props;
        const noTransferSystem = getTransferSystemStatus().type === 'none';
        const runningInMobile = runningWithAsperaMobile();
        const itemToString = (element) => {
            return element === null || element === void 0 ? void 0 : element.text;
        };
        if (loading) {
            return React.createElement(Loading, { description: language.get('pageSystem.loading'), className: "center-loading" });
        }
        else {
            const inactiveTransfers = getInactiveTransfers(this.transfers);
            return (React.createElement("div", null,
                React.createElement("div", { className: "dropdown-wrapper" },
                    React.createElement(Dropdown, { id: "transfer-activity-activity", "data-auto": "transfer-activity-dropdown-activity", onChange: this.changeActivity, items: this.activityOptions, itemToString: itemToString, titleText: language.get('transferActivity.allActivity'), label: language.get('transferActivity.allActivity'), "aria-label": language.get('transferActivity.allActivity') }),
                    React.createElement(Dropdown, { id: "transfer-activity-status", "data-auto": "transfer-activity-dropdown-status", onChange: this.changeStatus, items: this.statusOptions, itemToString: itemToString, titleText: language.get('transferActivity.allStatus'), label: language.get('transferActivity.allStatus'), "aria-label": language.get('transferActivity.allStatus') }),
                    !!(fullPageMode && this.transfers.length) && React.createElement("div", { className: "button-wrapper" },
                        React.createElement(Button, { kind: "ghost", onClick: this.clearInactive }, language.get('transferActivity.clearInactiveTransfers')))),
                noTransferSystem && this.transferError,
                runningInMobile && this.mobileWarning,
                React.createElement("div", { className: "advertisement-area" }, this.advertisingItems),
                !!inactiveTransfers.length && React.createElement(Link, { className: "clear-inactive-transfers", onClick: this.clearInactive },
                    " ",
                    language.get('transferActivity.clearInactiveTransfersCount', [inactiveTransfers.length]),
                    " "),
                React.createElement("div", { className: "transfer-list", "data-auto": "transfer-activity-transfer-list" }, this.transfers.length ? this.getTransferList() : this.getEmptyState())));
        }
    }
    componentWillUnmount() {
        this.subManager.unsubscribeAll();
    }
    componentDidMount() {
        this.subManager.setSubscription(getAllCurrentTransfers().subscribe({
            next: transferData => {
                this.transfers = transferData.transfers;
                this.transferUpdate.next(this.transfers.map(item => new AsperaTransfer(item)));
                this.setState({ loading: false });
            },
            error: error => {
                new AsperaError('AsperaTransferActivity: Unable to get initial transfers', error);
                this.setState({ loading: false });
            },
        }));
        this.subManager.setSubscription(monitorTransfers().subscribe({
            next: transferData => {
                this.transfers = transferData.transfers;
                this.transferUpdate.next(this.transfers.map(item => new AsperaTransfer(item)));
                this.setState({ loading: false });
            },
            error: error => {
                new AsperaError('AsperaTransferActivity: Unable to monitor transfers', error);
                this.setState({ loading: false });
            },
        }));
    }
    componentDidUpdate(previousProps) {
        const { open } = this.props;
        // Reset drop down menu when closing
        if (open !== previousProps.open && !open) {
            this.filterOptions.activity = '';
            this.filterOptions.status = '';
        }
    }
    render() {
        const { className, onClose, open, fullPageMode } = this.props;
        let mainContent = (React.createElement(AsperaSlideOverPanel, { open: open, onClose: onClose, title: language.get('transferActivity.transferActivity'), className: `aspera-transfer-activity aspera-ide-override ${className ? className : ''}`, automationTag: "transfer-activity-wrapper", overflowOptions: this.panelOptions, footer: this.panelFooter }, open && this.getBody()));
        if (fullPageMode) {
            mainContent = (React.createElement("div", { className: `aspera-transfer-activity full-page-mode aspera-ide-override ${className ? className : ''}` },
                React.createElement("div", { className: "transfer-activity-full-page-header" }, language.get('transferActivity.transferActivity')),
                this.getBody()));
        }
        return (React.createElement(Theme, { theme: tracking.currentTheme }, mainContent));
    }
}
